import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { DEFAULT_DASHBOARD_TABS } from './domains/settings/dashboardTabs';
import './CompanySetupWizard.css';

const steps = ['Company', 'Start Mode', 'Workspace', 'Operations', 'Forms', 'Access', 'Review'];

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata', label: 'India (Asia/Kolkata)' },
  { value: 'America/New_York', label: 'US Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'US Central (America/Chicago)' },
  { value: 'America/Denver', label: 'US Mountain (America/Denver)' },
  { value: 'America/Phoenix', label: 'US Mountain - Arizona (America/Phoenix)' },
  { value: 'America/Los_Angeles', label: 'US Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'US Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'US Hawaii (Pacific/Honolulu)' },
  { value: 'America/Puerto_Rico', label: 'US Puerto Rico (America/Puerto_Rico)' },
  { value: 'Pacific/Guam', label: 'US Guam (Pacific/Guam)' },
  { value: 'Pacific/Pago_Pago', label: 'US American Samoa (Pacific/Pago_Pago)' },
  { value: 'America/St_Thomas', label: 'US Virgin Islands (America/St_Thomas)' }
];

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' }
];

const DEFAULT_TRANSACTION_SCHEMA = {
  quantity_unit: 'Kg',
  include_date: true,
  include_notes: true,
  fields: [
    { key: 'type', label: 'Transaction Type', type: 'dropdown', required: true, options: ['in', 'out'] },
    { key: 'quantity', label: 'Quantity', type: 'number', required: true, options: [] }
  ],
  stock_field_key: 'quantity',
  type_field_key: 'type',
  grouping_keys: []
};
const DEFAULT_FINISHED_GOODS_TRANSACTION_SCHEMA = {
  ...DEFAULT_TRANSACTION_SCHEMA,
  quantity_unit: 'Pcs'
};

const DEFAULT_PRODUCT_SCHEMA = {
  fields: []
};

const DEFAULT_STAFF_SCHEMA = {
  fields: []
};

const DEFAULT_CUSTOMER_SCHEMA = {
  fields: []
};

const DEFAULT_CONTRACTOR_SCHEMA = {
  fields: []
};

const cloneValue = (value) => JSON.parse(JSON.stringify(value));
const getDefaultDashboardTabsState = () => cloneValue(DEFAULT_DASHBOARD_TABS);
const getDefaultThresholds = () => ({
  critical: '50',
  low: '100',
  medium: '200',
  orderNumberStart: '1001'
});

const buildFieldCountSummary = (schema) => Array.isArray(schema?.fields) ? schema.fields.length : 0;
const buildPresetTabs = (visibleIds) =>
  DEFAULT_DASHBOARD_TABS.map((tab) => ({ ...cloneValue(tab), visible: visibleIds.includes(tab.id) }));
const buildCustomField = (key, label, type = 'text', required = false, options = [], unit = '') => ({
  key,
  label,
  type,
  required,
  options,
  unit
});

const INDUSTRY_PRESETS = [
  {
    id: 'general_manufacturing',
    label: 'General Manufacturing',
    description: 'Balanced setup for companies that buy materials, build products, and move orders through a production flow.',
    defaults: { critical: '50', low: '120', medium: '250', orderNumberStart: '1001' },
    dashboardTabs: buildPresetTabs(['add', 'transactions', 'stock', 'insights', 'pipeline_board', 'product_add', 'products', 'order', 'orders', 'staff', 'customers', 'settings']),
    pipeline: ['Raw Material Intake', 'Production', 'Quality Check', 'Packing', 'Dispatch'],
    rawMaterials: [
      {
        name: 'Primary Material',
        unitLabel: 'Kg',
        is_main: true,
        characteristics: [
          { name: 'Grade', mandatory: true },
          { name: 'Specification', mandatory: false }
        ]
      },
      {
        name: 'Packaging',
        unitLabel: 'Pcs',
        characteristics: [{ name: 'Type', mandatory: true }]
      }
    ],
    productSchema: {
      fields: [
        buildCustomField('sku', 'SKU', 'text', true),
        buildCustomField('finish', 'Finish', 'dropdown', false, ['Standard', 'Premium'])
      ]
    },
    staffSchema: {
      fields: [buildCustomField('shift', 'Shift', 'dropdown', false, ['Day', 'Night'])]
    }
  },
  {
    id: 'inventory_trading',
    label: 'Inventory and Trading',
    description: 'Focused on inward and outward stock movement with lighter production setup and stronger customer/order visibility.',
    defaults: { critical: '20', low: '60', medium: '120', orderNumberStart: '5001' },
    dashboardTabs: buildPresetTabs(['add', 'transactions', 'stock', 'insights', 'products', 'order', 'orders', 'customers', 'settings']),
    pipeline: ['Inward', 'Available', 'Picked', 'Dispatch'],
    rawMaterials: [
      {
        name: 'Stock Item',
        unitLabel: 'Pcs',
        is_main: true,
        characteristics: [
          { name: 'SKU', mandatory: true },
          { name: 'Variant', mandatory: false }
        ]
      }
    ],
    productSchema: {
      fields: [
        buildCustomField('sku', 'SKU', 'text', true),
        buildCustomField('brand', 'Brand', 'text', false)
      ]
    },
    customerSchema: {
      fields: [buildCustomField('region', 'Region', 'text', false)]
    }
  },
  {
    id: 'job_work',
    label: 'Job Work and Contractors',
    description: 'Designed for subcontracted workflows with stronger staff and contractor tracking.',
    defaults: { critical: '30', low: '80', medium: '160', orderNumberStart: '3001' },
    dashboardTabs: buildPresetTabs(['add', 'transactions', 'stock', 'insights', 'pipeline_board', 'order', 'orders', 'staff', 'contractors', 'customers', 'settings']),
    pipeline: ['Received', 'Assigned', 'In Process', 'Returned', 'Closed'],
    rawMaterials: [
      {
        name: 'Input Material',
        unitLabel: 'Kg',
        is_main: true,
        characteristics: [
          { name: 'Type', mandatory: true },
          { name: 'Size', mandatory: false }
        ]
      }
    ],
    staffSchema: {
      fields: [
        buildCustomField('specialty', 'Specialty', 'text', false),
        buildCustomField('shift', 'Shift', 'dropdown', false, ['Day', 'Night'])
      ]
    },
    contractorSchema: {
      fields: [buildCustomField('service_scope', 'Service Scope', 'text', true)]
    }
  },
  {
    id: 'retail_finished_goods',
    label: 'Retail Finished Goods',
    description: 'Best for companies focused on ready stock, finished goods orders, and customer fulfilment.',
    defaults: { critical: '15', low: '40', medium: '90', orderNumberStart: '7001' },
    dashboardTabs: buildPresetTabs(['add', 'transactions', 'stock', 'insights', 'products', 'order', 'orders', 'customers', 'settings']),
    pipeline: ['Available', 'Allocated', 'Packed', 'Shipped'],
    rawMaterials: [
      {
        name: 'Finished Material',
        unitLabel: 'Pcs',
        is_main: true,
        characteristics: [
          { name: 'Style', mandatory: true },
          { name: 'Size', mandatory: false }
        ]
      },
      {
        name: 'Packaging',
        unitLabel: 'Pcs',
        characteristics: [{ name: 'Type', mandatory: true }]
      }
    ],
    productSchema: {
      fields: [
        buildCustomField('sku', 'SKU', 'text', true),
        buildCustomField('mrp', 'MRP', 'number', false, [], 'USD')
      ]
    },
    customerSchema: {
      fields: [buildCustomField('delivery_zone', 'Delivery Zone', 'text', false)]
    }
  }
];

function CompanySetupWizard({ onLogout }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdCompany, setCreatedCompany] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [companies, setCompanies] = useState([]);
  const [setupMode, setSetupMode] = useState('fresh');
  const [industryPresetId, setIndustryPresetId] = useState('');
  const [templateCompanyId, setTemplateCompanyId] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);

  const [company, setCompany] = useState({
    name: '',
    slug: '',
    timezone: 'Asia/Kolkata',
    activateNow: false
  });

  const [defaults, setDefaults] = useState(getDefaultThresholds);

  const [admin, setAdmin] = useState({
    forceReset: true
  });

  const [pipelineStages, setPipelineStages] = useState([]);
  const [newStage, setNewStage] = useState('');
  const [rawMaterials, setRawMaterials] = useState([]);
  const [newRawMaterial, setNewRawMaterial] = useState({
    name: '',
    unitLabel: ''
  });
  const [newRawMaterialCharacteristic, setNewRawMaterialCharacteristic] = useState({
    name: '',
    mandatory: false
  });

  const [rawMaterialTransactionSchema, setRawMaterialTransactionSchema] = useState(DEFAULT_TRANSACTION_SCHEMA);
  const [finishedGoodsTransactionSchema, setFinishedGoodsTransactionSchema] = useState(DEFAULT_FINISHED_GOODS_TRANSACTION_SCHEMA);
  const [transactionSchemaMode, setTransactionSchemaMode] = useState('raw_material');
  const [productSchema, setProductSchema] = useState(DEFAULT_PRODUCT_SCHEMA);
  const [staffSchema, setStaffSchema] = useState(DEFAULT_STAFF_SCHEMA);
  const [customerSchema, setCustomerSchema] = useState(DEFAULT_CUSTOMER_SCHEMA);
  const [contractorSchema, setContractorSchema] = useState(DEFAULT_CONTRACTOR_SCHEMA);
  const [newField, setNewField] = useState({
    label: '',
    type: 'text',
    required: false,
    options: '',
    unit: ''
  });
  const [newProductField, setNewProductField] = useState({
    label: '',
    type: 'text',
    required: false,
    options: '',
    unit: ''
  });
  const [newStaffField, setNewStaffField] = useState({
    label: '',
    type: 'text',
    required: false,
    options: '',
    unit: ''
  });
  const [newCustomerField, setNewCustomerField] = useState({
    label: '',
    type: 'text',
    required: false,
    options: '',
    unit: ''
  });
  const [newContractorField, setNewContractorField] = useState({
    label: '',
    type: 'text',
    required: false,
    options: '',
    unit: ''
  });
  const [dashboardTabs, setDashboardTabs] = useState(getDefaultDashboardTabsState);

  const previewTabsFiltered = dashboardTabs.filter((tab) => tab.visible !== false);

  const renderPreviewIcon = (tabId) => {
    const iconProps = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none' };
    switch (tabId) {
      case 'add':
        return (
          <svg {...iconProps} stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" strokeLinecap="round" />
          </svg>
        );
      case 'transactions':
        return (
          <svg {...iconProps} stroke="currentColor" strokeWidth="2">
            <path d="M4 7h16M4 12h16M4 17h10" strokeLinecap="round" />
          </svg>
        );
      case 'stock':
        return (
          <svg {...iconProps} stroke="currentColor" strokeWidth="2">
            <path d="M4 19V9l4-4 4 4 4-4 4 4v10" strokeLinecap="round" />
          </svg>
        );
      case 'insights':
        return (
          <svg {...iconProps} stroke="currentColor" strokeWidth="2">
            <path d="M5 18l4-5 3 2 5-7" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 6v12h14" strokeLinecap="round" />
          </svg>
        );
      case 'pipeline_board':
        return (
          <svg {...iconProps} stroke="currentColor" strokeWidth="2">
            <path d="M4 7h16M7 7v10M17 7v10M7 17h10" strokeLinecap="round" />
            <path d="M9 13h2m2 0h2" strokeLinecap="round" />
          </svg>
        );
      case 'product_add':
        return (
          <svg {...iconProps} stroke="currentColor" strokeWidth="2">
            <path d="M4 7h12l4 4v10H4z" strokeLinejoin="round" />
            <path d="M12 7V5m0 2h-2m2 0h2" strokeLinecap="round" />
          </svg>
        );
      case 'products':
        return (
          <svg {...iconProps} stroke="currentColor" strokeWidth="2">
            <path d="M5 6h14v12H5z" strokeLinejoin="round" />
            <path d="M5 10h14" strokeLinecap="round" />
          </svg>
        );
      case 'order':
        return (
          <svg {...iconProps} stroke="currentColor" strokeWidth="2">
            <path d="M6 7h12l-1 12H7L6 7z" strokeLinejoin="round" />
            <path d="M9 7V5a3 3 0 016 0v2" strokeLinecap="round" />
          </svg>
        );
      case 'orders':
        return (
          <svg {...iconProps} stroke="currentColor" strokeWidth="2">
            <path d="M6 3h12v18H6z" strokeLinejoin="round" />
            <path d="M9 8h6M9 12h6M9 16h4" strokeLinecap="round" />
          </svg>
        );
      case 'staff':
        return (
          <svg {...iconProps} stroke="currentColor" strokeWidth="2">
            <path d="M16 11a4 4 0 10-8 0" strokeLinecap="round" />
            <path d="M4 19c1.5-3 4.5-5 8-5s6.5 2 8 5" strokeLinecap="round" />
          </svg>
        );
      case 'customers':
        return (
          <svg {...iconProps} stroke="currentColor" strokeWidth="2">
            <path d="M8 6a4 4 0 108 0" strokeLinecap="round" />
            <path d="M3 20c1.5-3.5 5-6 9-6s7.5 2.5 9 6" strokeLinecap="round" />
          </svg>
        );
      case 'contractors':
        return (
          <svg {...iconProps} stroke="currentColor" strokeWidth="2">
            <path d="M9 6a3 3 0 106 0" strokeLinecap="round" />
            <path d="M4 19c1.5-3 4.5-5 8-5s6.5 2 8 5" strokeLinecap="round" />
            <path d="M4 9h4M16 9h4" strokeLinecap="round" />
          </svg>
        );
      case 'settings':
        return (
          <svg {...iconProps} stroke="currentColor" strokeWidth="2">
            <path
              d="M12 7a5 5 0 000 10 5 5 0 000-10zm8 5a7.9 7.9 0 00-.1-1l2.1-1.6-2-3.4-2.5 1a8.1 8.1 0 00-1.7-1l-.4-2.7H9.6l-.4 2.7a8.1 8.1 0 00-1.7 1l-2.5-1-2 3.4L5 11a7.9 7.9 0 000 2l-2.1 1.6 2 3.4 2.5-1a8.1 8.1 0 001.7 1l.4 2.7h4.8l.4-2.7a8.1 8.1 0 001.7-1l2.5 1 2-3.4L19.9 13c.1-.3.1-.6.1-1z"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const data = await api.getCompanies();
        setCompanies(data || []);
      } catch (_err) {
        setCompanies([]);
      }
    };
    loadCompanies();
  }, []);

  const resetSetupState = () => {
    setDefaults(getDefaultThresholds());
    setPipelineStages([]);
    setRawMaterials([]);
    setRawMaterialTransactionSchema(cloneValue(DEFAULT_TRANSACTION_SCHEMA));
    setFinishedGoodsTransactionSchema(cloneValue(DEFAULT_FINISHED_GOODS_TRANSACTION_SCHEMA));
    setProductSchema(cloneValue(DEFAULT_PRODUCT_SCHEMA));
    setStaffSchema(cloneValue(DEFAULT_STAFF_SCHEMA));
    setCustomerSchema(cloneValue(DEFAULT_CUSTOMER_SCHEMA));
    setContractorSchema(cloneValue(DEFAULT_CONTRACTOR_SCHEMA));
    setDashboardTabs(getDefaultDashboardTabsState());
    setTransactionSchemaMode('raw_material');
  };

  const applyIndustryPreset = (presetId) => {
    const preset = INDUSTRY_PRESETS.find((item) => item.id === presetId);
    setIndustryPresetId(presetId);
    setSetupMode('fresh');
    setTemplateCompanyId('');
    setError('');
    if (!preset) {
      resetSetupState();
      return;
    }

    setDefaults({
      critical: String(preset.defaults?.critical ?? '50'),
      low: String(preset.defaults?.low ?? '100'),
      medium: String(preset.defaults?.medium ?? '200'),
      orderNumberStart: String(preset.defaults?.orderNumberStart ?? '1001')
    });
    setPipelineStages(cloneValue(preset.pipeline || []));
    setRawMaterials(normalizeWizardRawMaterials(preset.rawMaterials || []));
    setRawMaterialTransactionSchema(cloneValue(DEFAULT_TRANSACTION_SCHEMA));
    setFinishedGoodsTransactionSchema(cloneValue(DEFAULT_FINISHED_GOODS_TRANSACTION_SCHEMA));
    setProductSchema({
      ...cloneValue(DEFAULT_PRODUCT_SCHEMA),
      ...(preset.productSchema ? cloneValue(preset.productSchema) : {})
    });
    setStaffSchema({
      ...cloneValue(DEFAULT_STAFF_SCHEMA),
      ...(preset.staffSchema ? cloneValue(preset.staffSchema) : {})
    });
    setCustomerSchema({
      ...cloneValue(DEFAULT_CUSTOMER_SCHEMA),
      ...(preset.customerSchema ? cloneValue(preset.customerSchema) : {})
    });
    setContractorSchema({
      ...cloneValue(DEFAULT_CONTRACTOR_SCHEMA),
      ...(preset.contractorSchema ? cloneValue(preset.contractorSchema) : {})
    });
    setDashboardTabs(
      Array.isArray(preset.dashboardTabs) && preset.dashboardTabs.length
        ? cloneValue(preset.dashboardTabs)
        : getDefaultDashboardTabsState()
    );
    setTransactionSchemaMode('raw_material');
  };

  const applyTemplateToWizard = async (companyId) => {
    if (!companyId) {
      resetSetupState();
      return;
    }

    setTemplateLoading(true);
    try {
      const [template, labels] = await Promise.all([
        api.getCompanyTemplate(companyId),
        api.getCompanyLabels(companyId)
      ]);
      setRawMaterialTransactionSchema({
        ...cloneValue(DEFAULT_TRANSACTION_SCHEMA),
        ...(template.raw_material_transaction_schema || template.transaction_schema || {})
      });
      setFinishedGoodsTransactionSchema({
        ...cloneValue(DEFAULT_FINISHED_GOODS_TRANSACTION_SCHEMA),
        ...(template.finished_goods_transaction_schema || template.transaction_schema || {})
      });
      setProductSchema({
        ...cloneValue(DEFAULT_PRODUCT_SCHEMA),
        ...(template.product_schema || {})
      });
      setStaffSchema({
        ...cloneValue(DEFAULT_STAFF_SCHEMA),
        ...(template.staff_schema || {})
      });
      setCustomerSchema({
        ...cloneValue(DEFAULT_CUSTOMER_SCHEMA),
        ...(template.customer_schema || {})
      });
      setContractorSchema({
        ...cloneValue(DEFAULT_CONTRACTOR_SCHEMA),
        ...(template.contractor_schema || {})
      });
      setDefaults({
        critical: String(template.defaults?.critical ?? '50'),
        low: String(template.defaults?.low ?? '100'),
        medium: String(template.defaults?.medium ?? '200'),
        orderNumberStart: String(template.defaults?.orderNumberStart ?? '1001')
      });
      setPipelineStages(Array.isArray(template.material_pipeline) ? template.material_pipeline : []);
      setRawMaterials(normalizeWizardRawMaterials(template.raw_materials));
      setDashboardTabs(
        Array.isArray(labels?.dashboard_tabs) && labels.dashboard_tabs.length
          ? cloneValue(labels.dashboard_tabs)
          : getDefaultDashboardTabsState()
      );
    } catch (_err) {
      resetSetupState();
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleSetupModeChange = async (mode) => {
    setSetupMode(mode);
    setError('');
    if (mode !== 'clone') {
      setTemplateCompanyId('');
      if (industryPresetId) {
        applyIndustryPreset(industryPresetId);
      } else {
        resetSetupState();
      }
    }
  };

  const setDashboardTabVisibility = (tabId, visible) => {
    setDashboardTabs((prev) =>
      prev.map((tab) =>
        tab.id === tabId
          ? { ...tab, visible }
          : tab
      )
    );
  };

  const applyWorkspacePreset = (preset) => {
    const coreTabs = new Set([
      'add',
      'transactions',
      'stock',
      'insights',
      'pipeline_board',
      'product_add',
      'products',
      'order',
      'orders',
      'settings'
    ]);
    const operationalTabs = new Set([
      'add',
      'transactions',
      'stock',
      'insights',
      'pipeline_board',
      'product_add',
      'products',
      'order',
      'orders'
    ]);
    setDashboardTabs((prev) =>
      prev.map((tab) => {
        if (preset === 'all') return { ...tab, visible: true };
        if (preset === 'core') return { ...tab, visible: coreTabs.has(tab.id) };
        if (preset === 'operations') return { ...tab, visible: operationalTabs.has(tab.id) };
        return tab;
      })
    );
  };

  const enabledTabCount = dashboardTabs.filter((tab) => tab.visible !== false).length;
  const peopleFieldCount =
    buildFieldCountSummary(staffSchema) +
    buildFieldCountSummary(customerSchema) +
    buildFieldCountSummary(contractorSchema);
  const selectedTemplateCompany = companies.find((entry) => String(entry.id) === String(templateCompanyId));
  const selectedIndustryPreset = INDUSTRY_PRESETS.find((preset) => preset.id === industryPresetId);

  const transactionSchema = transactionSchemaMode === 'finished_goods'
    ? finishedGoodsTransactionSchema
    : rawMaterialTransactionSchema;

  const updateSchema = (updates) => {
    if (transactionSchemaMode === 'finished_goods') {
      setFinishedGoodsTransactionSchema((prev) => ({ ...prev, ...updates }));
      return;
    }
    setRawMaterialTransactionSchema((prev) => ({ ...prev, ...updates }));
  };

  const updateProductSchema = (updates) => {
    setProductSchema((prev) => ({ ...prev, ...updates }));
  };

  const updateStaffSchema = (updates) => {
    setStaffSchema((prev) => ({ ...prev, ...updates }));
  };

  const updateCustomerSchema = (updates) => {
    setCustomerSchema((prev) => ({ ...prev, ...updates }));
  };

  const updateContractorSchema = (updates) => {
    setContractorSchema((prev) => ({ ...prev, ...updates }));
  };

  const slugifyFieldKey = (label) =>
    label
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const addField = () => {
    if (!newField.label.trim()) return;
    const baseKey = slugifyFieldKey(newField.label) || 'field';
    let uniqueKey = baseKey;
    let counter = 1;
    const existingKeys = transactionSchema.fields.map((field) => field.key);
    while (existingKeys.includes(uniqueKey)) {
      uniqueKey = `${baseKey}_${counter}`;
      counter += 1;
    }
    const options = newField.type === 'dropdown'
      ? newField.options.split(',').map((opt) => opt.trim()).filter(Boolean)
      : [];
    updateSchema({
      fields: [
        ...transactionSchema.fields,
        {
          key: uniqueKey,
          label: newField.label.trim(),
          type: newField.type,
          required: newField.required,
          options,
          unit: newField.type === 'number' ? newField.unit.trim() : ''
        }
      ]
    });
    setNewField({ label: '', type: 'text', required: false, options: '', unit: '' });
  };

  const addProductField = () => {
    if (!newProductField.label.trim()) return;
    const baseKey = slugifyFieldKey(newProductField.label) || 'field';
    let uniqueKey = baseKey;
    let counter = 1;
    const existingKeys = productSchema.fields.map((field) => field.key);
    while (existingKeys.includes(uniqueKey)) {
      uniqueKey = `${baseKey}_${counter}`;
      counter += 1;
    }
    const options = newProductField.type === 'dropdown'
      ? newProductField.options.split(',').map((opt) => opt.trim()).filter(Boolean)
      : [];
    updateProductSchema({
      fields: [
        ...productSchema.fields,
        {
          key: uniqueKey,
          label: newProductField.label.trim(),
          type: newProductField.type,
          required: newProductField.required,
          options,
          unit: newProductField.type === 'number' ? newProductField.unit.trim() : ''
        }
      ]
    });
    setNewProductField({ label: '', type: 'text', required: false, options: '', unit: '' });
  };

  const addStaffField = () => {
    if (!newStaffField.label.trim()) return;
    const baseKey = slugifyFieldKey(newStaffField.label) || 'field';
    let uniqueKey = baseKey;
    let counter = 1;
    const existingKeys = staffSchema.fields.map((field) => field.key);
    while (existingKeys.includes(uniqueKey)) {
      uniqueKey = `${baseKey}_${counter}`;
      counter += 1;
    }
    const options = newStaffField.type === 'dropdown'
      ? newStaffField.options.split(',').map((opt) => opt.trim()).filter(Boolean)
      : [];
    updateStaffSchema({
      fields: [
        ...staffSchema.fields,
        {
          key: uniqueKey,
          label: newStaffField.label.trim(),
          type: newStaffField.type,
          required: newStaffField.required,
          options,
          unit: newStaffField.type === 'number' ? newStaffField.unit.trim() : ''
        }
      ]
    });
    setNewStaffField({ label: '', type: 'text', required: false, options: '', unit: '' });
  };

  const addCustomerField = () => {
    if (!newCustomerField.label.trim()) return;
    const baseKey = slugifyFieldKey(newCustomerField.label) || 'field';
    let uniqueKey = baseKey;
    let counter = 1;
    const existingKeys = customerSchema.fields.map((field) => field.key);
    while (existingKeys.includes(uniqueKey)) {
      uniqueKey = `${baseKey}_${counter}`;
      counter += 1;
    }
    const options = newCustomerField.type === 'dropdown'
      ? newCustomerField.options.split(',').map((opt) => opt.trim()).filter(Boolean)
      : [];
    updateCustomerSchema({
      fields: [
        ...customerSchema.fields,
        {
          key: uniqueKey,
          label: newCustomerField.label.trim(),
          type: newCustomerField.type,
          required: newCustomerField.required,
          options,
          unit: newCustomerField.type === 'number' ? newCustomerField.unit.trim() : ''
        }
      ]
    });
    setNewCustomerField({ label: '', type: 'text', required: false, options: '', unit: '' });
  };

  const addContractorField = () => {
    if (!newContractorField.label.trim()) return;
    const baseKey = slugifyFieldKey(newContractorField.label) || 'field';
    let uniqueKey = baseKey;
    let counter = 1;
    const existingKeys = contractorSchema.fields.map((field) => field.key);
    while (existingKeys.includes(uniqueKey)) {
      uniqueKey = `${baseKey}_${counter}`;
      counter += 1;
    }
    const options = newContractorField.type === 'dropdown'
      ? newContractorField.options.split(',').map((opt) => opt.trim()).filter(Boolean)
      : [];
    updateContractorSchema({
      fields: [
        ...contractorSchema.fields,
        {
          key: uniqueKey,
          label: newContractorField.label.trim(),
          type: newContractorField.type,
          required: newContractorField.required,
          options,
          unit: newContractorField.type === 'number' ? newContractorField.unit.trim() : ''
        }
      ]
    });
    setNewContractorField({ label: '', type: 'text', required: false, options: '', unit: '' });
  };

  const updateField = (key, updates) => {
    updateSchema({
      fields: transactionSchema.fields.map((field) =>
        field.key === key ? { ...field, ...updates } : field
      )
    });
  };

  const updateProductField = (key, updates) => {
    updateProductSchema({
      fields: productSchema.fields.map((field) =>
        field.key === key ? { ...field, ...updates } : field
      )
    });
  };

  const updateStaffField = (key, updates) => {
    updateStaffSchema({
      fields: staffSchema.fields.map((field) =>
        field.key === key ? { ...field, ...updates } : field
      )
    });
  };

  const updateCustomerField = (key, updates) => {
    updateCustomerSchema({
      fields: customerSchema.fields.map((field) =>
        field.key === key ? { ...field, ...updates } : field
      )
    });
  };

  const updateContractorField = (key, updates) => {
    updateContractorSchema({
      fields: contractorSchema.fields.map((field) =>
        field.key === key ? { ...field, ...updates } : field
      )
    });
  };

  const moveField = (key, direction) => {
    const index = transactionSchema.fields.findIndex((field) => field.key === key);
    if (index === -1) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= transactionSchema.fields.length) return;
    const next = [...transactionSchema.fields];
    const [item] = next.splice(index, 1);
    next.splice(targetIndex, 0, item);
    updateSchema({ fields: next });
  };

  const moveProductField = (key, direction) => {
    const index = productSchema.fields.findIndex((field) => field.key === key);
    if (index === -1) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= productSchema.fields.length) return;
    const next = [...productSchema.fields];
    const [item] = next.splice(index, 1);
    next.splice(targetIndex, 0, item);
    updateProductSchema({ fields: next });
  };

  const moveStaffField = (key, direction) => {
    const index = staffSchema.fields.findIndex((field) => field.key === key);
    if (index === -1) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= staffSchema.fields.length) return;
    const next = [...staffSchema.fields];
    const [item] = next.splice(index, 1);
    next.splice(targetIndex, 0, item);
    updateStaffSchema({ fields: next });
  };

  const moveCustomerField = (key, direction) => {
    const index = customerSchema.fields.findIndex((field) => field.key === key);
    if (index === -1) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= customerSchema.fields.length) return;
    const next = [...customerSchema.fields];
    const [item] = next.splice(index, 1);
    next.splice(targetIndex, 0, item);
    updateCustomerSchema({ fields: next });
  };

  const moveContractorField = (key, direction) => {
    const index = contractorSchema.fields.findIndex((field) => field.key === key);
    if (index === -1) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= contractorSchema.fields.length) return;
    const next = [...contractorSchema.fields];
    const [item] = next.splice(index, 1);
    next.splice(targetIndex, 0, item);
    updateContractorSchema({ fields: next });
  };

  const removeField = (key) => {
    if (key === 'quantity' || key === 'type') return;
    updateSchema({
      fields: transactionSchema.fields.filter((field) => field.key !== key)
    });
    if (transactionSchema.stock_field_key === key) {
      updateSchema({ stock_field_key: 'quantity' });
    }
    if (transactionSchema.type_field_key === key) {
      updateSchema({ type_field_key: 'type' });
    }
    if (transactionSchema.grouping_keys.includes(key)) {
      updateSchema({
        grouping_keys: transactionSchema.grouping_keys.filter((groupKey) => groupKey !== key)
      });
    }
  };

  const removeProductField = (key) => {
    updateProductSchema({
      fields: productSchema.fields.filter((field) => field.key !== key)
    });
  };

  const removeStaffField = (key) => {
    updateStaffSchema({
      fields: staffSchema.fields.filter((field) => field.key !== key)
    });
  };

  const removeCustomerField = (key) => {
    updateCustomerSchema({
      fields: customerSchema.fields.filter((field) => field.key !== key)
    });
  };

  const removeContractorField = (key) => {
    updateContractorSchema({
      fields: contractorSchema.fields.filter((field) => field.key !== key)
    });
  };

  const addPipelineStage = () => {
    const value = newStage.trim();
    if (!value) return;
    setPipelineStages((prev) => [...prev, value]);
    setNewStage('');
  };

  const updatePipelineStage = (index, value) => {
    setPipelineStages((prev) => prev.map((stage, idx) => (idx === index ? value : stage)));
  };

  const movePipelineStage = (index, direction) => {
    setPipelineStages((prev) => {
      const next = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= next.length) return prev;
      const [item] = next.splice(index, 1);
      next.splice(targetIndex, 0, item);
      return next;
    });
  };

  const removePipelineStage = (index) => {
    setPipelineStages((prev) => prev.filter((_, idx) => idx !== index));
  };

  const toRawMaterialListKey = (value) =>
    `rm_${String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')}`;

  const normalizeRawMaterialCharacteristics = (characteristics, rawMaterialName = '') => {
    if (!Array.isArray(characteristics)) return [];
    const seen = new Set();
    return characteristics
      .map((item) => {
        const name = String(item?.name || item?.label || '').trim();
        if (!name) return null;
        const normalized = name.toLowerCase();
        if (seen.has(normalized)) return null;
        seen.add(normalized);
        const listKey = String(item?.list_key || '').trim();
        return {
          name,
          list_key: listKey || toRawMaterialListKey(`${rawMaterialName || 'material'}_${name}`),
          mandatory: Boolean(item?.mandatory)
        };
      })
      .filter(Boolean);
  };

  const normalizeWizardRawMaterials = (materials) => {
    if (!Array.isArray(materials)) return [];
    const normalized = materials
      .map((item) => {
        const name = String(item?.name || item?.label || '').trim();
        if (!name) return null;
        const characteristics = normalizeRawMaterialCharacteristics(
          item?.characteristics
            || (Array.isArray(item?.subcategories) && item.subcategories.length > 0
              ? [{ name: 'Type', list_key: toRawMaterialListKey(`${name}_type`), mandatory: true }]
              : []),
          name
        );
        return {
          name,
          unit_label: String(item?.unit_label || item?.unitLabel || '').trim(),
          characteristics,
          is_main: Boolean(item?.is_main)
        };
      })
      .filter(Boolean);
    if (normalized.length > 0 && !normalized.some((item) => item.is_main)) {
      normalized[0].is_main = true;
    }
    return normalized;
  };

  const addRawMaterial = () => {
    const name = newRawMaterial.name.trim();
    const unitLabel = newRawMaterial.unitLabel.trim();
    if (!name) return;
    if (rawMaterials.some((item) => item.name.toLowerCase() === name.toLowerCase())) {
      setError('Raw material name must be unique.');
      return;
    }
    const hasMain = rawMaterials.some((item) => item.is_main);
    setRawMaterials((prev) => [
      ...prev,
      { name, unit_label: unitLabel, characteristics: [], is_main: !hasMain }
    ]);
    setNewRawMaterial({ name: '', unitLabel: '' });
  };

  const updateRawMaterial = (index, updates) => {
    setRawMaterials((prev) => {
      const current = prev[index];
      if (!current) return prev;
      const nextName = String(updates?.name ?? current.name).trim();
      if (!nextName) return prev;
      const duplicate = prev.some((item, idx) => idx !== index && item.name.toLowerCase() === nextName.toLowerCase());
      if (duplicate) return prev;
      const nextItem = {
        ...current,
        ...updates,
        name: nextName,
        characteristics: normalizeRawMaterialCharacteristics(
          updates?.characteristics || current.characteristics || [],
          nextName
        )
      };
      if (updates.is_main) {
        return prev.map((item, idx) =>
          idx === index ? { ...nextItem, is_main: true } : { ...item, is_main: false }
        );
      }
      return prev.map((item, idx) => (idx === index ? nextItem : item));
    });
  };

  const removeRawMaterial = (index) => {
    setRawMaterials((prev) => {
      const next = prev.filter((_, idx) => idx !== index);
      if (next.length > 0 && !next.some((item) => item.is_main)) {
        next[0] = { ...next[0], is_main: true };
      }
      return next;
    });
  };

  const addRawMaterialCharacteristic = (rawIndex) => {
    const name = newRawMaterialCharacteristic.name.trim();
    if (!name) return;
    const material = rawMaterials[rawIndex];
    if (!material) return;
    if ((material.characteristics || []).some((item) => item.name.toLowerCase() === name.toLowerCase())) {
      setError(`"${name}" already exists for ${material.name}.`);
      return;
    }
    updateRawMaterial(rawIndex, {
      characteristics: [
        ...(material.characteristics || []),
        {
          name,
          list_key: toRawMaterialListKey(`${material.name}_${name}`),
          mandatory: Boolean(newRawMaterialCharacteristic.mandatory)
        }
      ]
    });
    setNewRawMaterialCharacteristic({ name: '', mandatory: false });
  };

  const updateRawMaterialCharacteristic = (rawIndex, characteristicIndex, updates) => {
    const material = rawMaterials[rawIndex];
    if (!material) return;
    const nextName = String(updates?.name || '').trim();
    if (
      nextName
      && (material.characteristics || []).some(
        (item, idx) => idx !== characteristicIndex && item.name.toLowerCase() === nextName.toLowerCase()
      )
    ) {
      setError(`"${nextName}" already exists for ${material.name}.`);
      return;
    }
    const next = (material.characteristics || []).map((item, idx) => {
      if (idx !== characteristicIndex) return item;
      const resolvedName = String(updates?.name ?? item.name).trim() || item.name;
      return {
        ...item,
        ...updates,
        name: resolvedName,
        list_key: toRawMaterialListKey(`${material.name}_${resolvedName}`)
      };
    });
    updateRawMaterial(rawIndex, { characteristics: next });
  };

  const removeRawMaterialCharacteristic = (rawIndex, characteristicIndex) => {
    const material = rawMaterials[rawIndex];
    if (!material) return;
    updateRawMaterial(rawIndex, {
      characteristics: (material.characteristics || []).filter((_, idx) => idx !== characteristicIndex)
    });
  };

  const handleNext = () => {
    setError('');
    if (step === 0 && !company.name.trim()) {
      setError('Company name is required.');
      return;
    }
    if (step === 1 && setupMode === 'clone' && !templateCompanyId) {
      setError('Select a company to clone from or switch back to start fresh.');
      return;
    }
    if (step === 2 && enabledTabCount === 0) {
      setError('Enable at least one workspace tab.');
      return;
    }
    if (step === 3) {
      if (rawMaterials.length === 0) {
        setError('Add at least one raw material type.');
        return;
      }
      if (!rawMaterials.some((item) => item.is_main)) {
        setError('Select a main raw material type.');
        return;
      }
    }
    if (step === 4) {
      const schemaChecks = [
        { label: 'Raw material', schema: rawMaterialTransactionSchema },
        { label: 'Finished goods', schema: finishedGoodsTransactionSchema }
      ];
      for (const item of schemaChecks) {
        const schema = item.schema || {};
        if (item.label !== 'Raw material' && !String(schema.quantity_unit || '').trim()) {
          setError(`${item.label} quantity unit is required.`);
          return;
        }
        if (!schema.type_field_key) {
          setError(`Select a dropdown field as the transaction type (In/Out) for ${item.label.toLowerCase()}.`);
          return;
        }
      }
    }
    setStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setError('');
    setStep((prev) => Math.max(prev - 1, 0));
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0] || null;
    setLogoFile(file);
    if (file) {
      const preview = URL.createObjectURL(file);
      setLogoPreview(preview);
    } else {
      setLogoPreview('');
    }
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!company.name.trim()) {
      setError('Company name is required.');
      return;
    }
    setLoading(true);
    try {
      const form = new FormData();
      const mainRawMaterial = rawMaterials.find((item) => item.is_main);
      const materialLabel = mainRawMaterial?.name || 'Material';
      form.append('name', company.name.trim());
      if (company.slug.trim()) form.append('slug', company.slug.trim());
      form.append('timezone', company.timezone.trim());
      form.append('stock_threshold_critical', defaults.critical);
      form.append('stock_threshold_low', defaults.low);
      form.append('stock_threshold_medium', defaults.medium);
      form.append('order_number_next', defaults.orderNumberStart);
      form.append('color_indicators_enabled', 'true');
      form.append('orders_enabled', 'true');
      form.append('material_label', materialLabel);
      form.append('material_pipeline', JSON.stringify(pipelineStages));
      form.append('raw_materials', JSON.stringify(normalizeWizardRawMaterials(rawMaterials)));
      form.append('product_schema', JSON.stringify(productSchema));
      form.append('transaction_schema', JSON.stringify(rawMaterialTransactionSchema));
      form.append('raw_material_transaction_schema', JSON.stringify(rawMaterialTransactionSchema));
      form.append('finished_goods_transaction_schema', JSON.stringify(finishedGoodsTransactionSchema));
      form.append('staff_schema', JSON.stringify(staffSchema));
      form.append('customer_schema', JSON.stringify(customerSchema));
      form.append('contractor_schema', JSON.stringify(contractorSchema));
      form.append('dashboard_tabs', JSON.stringify(dashboardTabs));
      form.append('activate_now', String(company.activateNow));
      form.append('force_password_reset', String(admin.forceReset));
      if (logoFile) {
        form.append('logo', logoFile);
      }

      const result = await api.createCompanySetup(form);
      setCreatedCompany(result);
      setSuccess(`Company created: ${result.name} (${result.slug}).`);
      setShowSuccessModal(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAsOwner = async () => {
    if (!createdCompany?.id) return;
    setError('');
    try {
      const data = await api.impersonateCompanyAdmin(createdCompany.id);
      const currentToken = api.getToken();
      const currentRefreshToken = api.getRefreshToken();
      const currentUser = localStorage.getItem('user');
      if (currentToken && currentUser) {
        localStorage.setItem('superadmin_token', currentToken);
        if (currentRefreshToken) {
          localStorage.setItem('superadmin_refresh_token', currentRefreshToken);
        }
        localStorage.setItem('superadmin_user', currentUser);
      }
      localStorage.setItem('impersonation_token', data.token);
      if (data.refresh_token) {
        localStorage.setItem('impersonation_refresh_token', data.refresh_token);
      }
      localStorage.setItem('impersonation_user', JSON.stringify(data.user));
      window.open('/impersonate', '_blank', 'noopener');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOpenWorkspaceAccess = () => {
    if (!createdCompany?.id) return;
    window.open(`/superadmin?workspaceCompanyId=${createdCompany.id}`, '_blank', 'noopener');
  };

  return (
    <div className="wizard-page">
      {showSuccessModal && (
        <div className="wizard-modal">
          <div className="wizard-modal-card">
            <h2>Company created</h2>
            <p>{success || 'The company has been created successfully.'}</p>
            <div className="wizard-success-actions">
              <button
                type="button"
                className="primary-button"
                onClick={handleOpenAsOwner}
              >
                Open as Owner
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={handleOpenWorkspaceAccess}
              >
                Open Workspace Access
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={() => {
                  setShowSuccessModal(false);
                  window.location.href = '/superadmin';
                }}
              >
                Return to Superadmin Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
      <header className="wizard-dashboard-header">
        <div>
          <h1>Create Company Workspace</h1>
          <p>Set up the company, choose the workspace, and prepare the first login experience.</p>
        </div>
        <div className="wizard-actions">
          <a href="/superadmin" className="secondary-link">Back to Dashboard</a>
          <button type="button" className="secondary-button" onClick={onLogout}>Logout</button>
        </div>
      </header>

      <div className="wizard-tabs">
        {steps.map((label, index) => (
          <button
            key={label}
            type="button"
            className={`wizard-tab ${index === step ? 'active' : ''}`}
            onClick={() => {
              if (index <= step) setStep(index);
            }}
            disabled={index > step}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <div className="wizard-banner error">{error}</div>}
      {success && <div className="wizard-banner success">{success}</div>}

      <section className="wizard-card">
        <div className="wizard-body">
          <div className="wizard-form">
            {step === 0 && (
              <div className="wizard-section">
                <h2>Company basics</h2>
                <div className="wizard-grid">
                  <label>
                    Company name
                    <input
                      type="text"
                      value={company.name}
                      onChange={(e) => setCompany({ ...company, name: e.target.value })}
                      placeholder="e.g. Atlas Textiles"
                    />
                  </label>
                  <label>
                    Company slug (optional)
                    <input
                      type="text"
                      value={company.slug}
                      onChange={(e) => setCompany({ ...company, slug: e.target.value })}
                      placeholder="atlas-textiles"
                    />
                  </label>
                  <label>
                    Timezone
                    <select
                      value={company.timezone}
                      onChange={(e) => setCompany({ ...company, timezone: e.target.value })}
                    >
                      {TIMEZONE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="logo-upload">
                    Company logo (optional)
                    <input type="file" accept="image/*" onChange={handleLogoChange} />
                    <div className="logo-preview">
                      <img
                        src={logoPreview || '/default-logo.svg'}
                        alt="Logo preview"
                      />
                    </div>
                  </label>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="wizard-section">
                <h2>Choose how to start</h2>
                <p>Pick the fastest path for this company. You can still customize the setup before creating it.</p>
                <div className="wizard-choice-grid">
                  <button
                    type="button"
                    className={`wizard-choice-card ${setupMode === 'fresh' ? 'active' : ''}`}
                    onClick={() => handleSetupModeChange('fresh')}
                  >
                    <span className="wizard-choice-kicker">Recommended</span>
                    <h3>Start fresh</h3>
                    <p>Use the standard app defaults, then tune workspace tabs, raw materials, forms, and access in the next steps.</p>
                  </button>
                  <button
                    type="button"
                    className={`wizard-choice-card ${setupMode === 'clone' ? 'active' : ''}`}
                    onClick={() => handleSetupModeChange('clone')}
                  >
                    <span className="wizard-choice-kicker">Fastest</span>
                    <h3>Clone existing company</h3>
                    <p>Copy another company’s workspace, pipeline, raw materials, and schema setup as the starting point.</p>
                  </button>
                </div>

                {setupMode === 'clone' && (
                  <div className="wizard-subsection">
                    <div className="wizard-grid">
                      <label>
                        Company to clone
                        <select
                          value={templateCompanyId}
                          onChange={async (e) => {
                            const value = e.target.value;
                            setTemplateCompanyId(value);
                            await applyTemplateToWizard(value);
                          }}
                        >
                          <option value="">Select company</option>
                          {companies.map((companyItem) => (
                            <option key={companyItem.id} value={companyItem.id}>
                              {companyItem.name} ({companyItem.slug})
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <p className="helper-text">
                      {templateLoading
                        ? 'Loading template settings...'
                        : 'This copies setup only. Live transactions, products, people records, and orders are not copied.'}
                    </p>
                  </div>
                )}

                {setupMode === 'fresh' && (
                  <div className="wizard-subsection">
                    <div className="wizard-subsection-header">
                      <div>
                        <h3>Built-in industry presets</h3>
                        <p className="helper-text">Choose a preset to prefill tabs, thresholds, pipeline stages, and starter fields.</p>
                      </div>
                      {industryPresetId && (
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={() => applyIndustryPreset('')}
                        >
                          Clear preset
                        </button>
                      )}
                    </div>
                    <div className="wizard-preset-grid">
                      {INDUSTRY_PRESETS.map((preset) => (
                        <button
                          key={preset.id}
                          type="button"
                          className={`wizard-preset-card ${industryPresetId === preset.id ? 'active' : ''}`}
                          onClick={() => applyIndustryPreset(preset.id)}
                        >
                          <span className="wizard-choice-kicker">Preset</span>
                          <h3>{preset.label}</h3>
                          <p>{preset.description}</p>
                          <div className="wizard-preset-meta">
                            <span>{preset.dashboardTabs.filter((tab) => tab.visible !== false).length} tabs</span>
                            <span>{preset.pipeline.length} stages</span>
                            <span>{preset.rawMaterials.length} material types</span>
                          </div>
                        </button>
                      ))}
                    </div>
                    {selectedIndustryPreset && (
                      <p className="helper-text">
                        Active preset: <strong>{selectedIndustryPreset.label}</strong>. You can still change every value in the later steps.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="wizard-section">
                <h2>Choose the workspace</h2>
                <p>Enable only the areas this company should see. These tabs will shape the owner and admin workspace on day one.</p>
                <div className="wizard-toolbar">
                  <button type="button" className="secondary-button" onClick={() => applyWorkspacePreset('core')}>
                    Core workspace
                  </button>
                  <button type="button" className="secondary-button" onClick={() => applyWorkspacePreset('operations')}>
                    Operations only
                  </button>
                  <button type="button" className="secondary-button" onClick={() => applyWorkspacePreset('all')}>
                    All tabs
                  </button>
                </div>
                <div className="wizard-workspace-grid">
                  {dashboardTabs.map((tab) => (
                    <label key={tab.id} className={`wizard-workspace-card ${tab.visible !== false ? 'active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={tab.visible !== false}
                        onChange={(e) => setDashboardTabVisibility(tab.id, e.target.checked)}
                      />
                      <div>
                        <strong>{tab.label}</strong>
                        <p>{tab.id === 'settings' ? 'Configuration and setup controls.' : 'Visible in the company navbar.'}</p>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="helper-text">{enabledTabCount} tabs enabled for this company.</p>
              </div>
            )}

            {step === 3 && (
              <div className="wizard-section">
                <h2>Operations foundation</h2>
                <p>Set the defaults that make the company operational: thresholds, pipeline stages, and the raw material structure.</p>
                <div className="wizard-grid">
                  <label>
                    Critical threshold
                    <input
                      type="number"
                      value={defaults.critical}
                      onChange={(e) => setDefaults({ ...defaults, critical: e.target.value })}
                    />
                  </label>
                  <label>
                    Low threshold
                    <input
                      type="number"
                      value={defaults.low}
                      onChange={(e) => setDefaults({ ...defaults, low: e.target.value })}
                    />
                  </label>
                  <label>
                    Medium threshold
                    <input
                      type="number"
                      value={defaults.medium}
                      onChange={(e) => setDefaults({ ...defaults, medium: e.target.value })}
                    />
                  </label>
                  <label>
                    Order number start
                    <input
                      type="number"
                      value={defaults.orderNumberStart}
                      onChange={(e) => setDefaults({ ...defaults, orderNumberStart: e.target.value })}
                    />
                  </label>
                </div>

                <div className="pipeline-builder">
                  <h3>Process pipeline</h3>
                  <p className="helper-text">Define the ordered flow from raw material to finished product.</p>
                  {pipelineStages.length === 0 ? (
                    <p className="helper-text">No stages yet. Add a few to describe the flow.</p>
                  ) : (
                    <div className="pipeline-list">
                      {pipelineStages.map((stage, index) => (
                        <div key={`pipeline-stage-${index}`} className="pipeline-row">
                          <div className="pipeline-order">
                            <button
                              type="button"
                              className="ghost-button"
                              onClick={() => movePipelineStage(index, 'up')}
                              disabled={index === 0}
                              title="Move up"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="ghost-button"
                              onClick={() => movePipelineStage(index, 'down')}
                              disabled={index === pipelineStages.length - 1}
                              title="Move down"
                            >
                              ↓
                            </button>
                          </div>
                          <input
                            type="text"
                            value={stage}
                            onChange={(e) => updatePipelineStage(index, e.target.value)}
                            placeholder="Stage name"
                          />
                          <button
                            type="button"
                            className="danger-icon-btn"
                            onClick={() => removePipelineStage(index)}
                            aria-label={`Remove stage ${stage || index + 1}`}
                            title="Remove stage"
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="pipeline-add">
                    <input
                      type="text"
                      value={newStage}
                      onChange={(e) => setNewStage(e.target.value)}
                      placeholder="e.g. Warehouse"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addPipelineStage();
                        }
                      }}
                    />
                    <button type="button" className="primary-button" onClick={addPipelineStage}>
                      Add Stage
                    </button>
                  </div>
                </div>

                <div className="raw-material-builder">
                  <h3>Raw materials</h3>
                  <p className="helper-text">Add Level 1 materials and Level 2 characteristics. Level 3 values are managed later in Settings → Manage Lists.</p>
                  {rawMaterials.length === 0 ? (
                    <p className="helper-text">No raw materials yet.</p>
                  ) : (
                    <div className="raw-material-list">
                      {rawMaterials.map((item, index) => (
                        <div key={`${item.name}-${index}`} className="raw-material-row">
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => updateRawMaterial(index, { name: e.target.value })}
                            placeholder="Category (e.g. Yarn)"
                          />
                          <input
                            type="text"
                            value={item.unit_label || ''}
                            onChange={(e) => updateRawMaterial(index, { unit_label: e.target.value })}
                            placeholder="Unit label (e.g. Kg, pcs)"
                          />
                          <label className="toggle raw-material-main">
                            <input
                              type="radio"
                              name="raw-material-main"
                              checked={Boolean(item.is_main)}
                              onChange={() => updateRawMaterial(index, { is_main: true })}
                            />
                            Main
                          </label>
                          <button
                            type="button"
                            className="danger-icon-btn"
                            onClick={() => removeRawMaterial(index)}
                            aria-label={`Remove raw material ${item.name || index + 1}`}
                            title="Remove raw material"
                          >
                            &times;
                          </button>
                          <div className="wizard-grid" style={{ marginTop: '8px' }}>
                            {(item.characteristics || []).map((characteristic, characteristicIndex) => (
                              <div key={`${item.name}-${characteristic.list_key}-${characteristicIndex}`} className="field-row">
                                <input
                                  type="text"
                                  value={characteristic.name}
                                  onChange={(e) =>
                                    updateRawMaterialCharacteristic(index, characteristicIndex, { name: e.target.value })
                                  }
                                  placeholder="Characteristic name"
                                />
                                <label className="toggle">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(characteristic.mandatory)}
                                    onChange={(e) =>
                                      updateRawMaterialCharacteristic(index, characteristicIndex, { mandatory: e.target.checked })
                                    }
                                  />
                                  Mandatory
                                </label>
                                <span className="helper-text">{characteristic.list_key}</span>
                                <button
                                  type="button"
                                  className="danger-icon-btn"
                                  onClick={() => removeRawMaterialCharacteristic(index, characteristicIndex)}
                                  aria-label={`Remove characteristic ${characteristic.name || characteristicIndex + 1}`}
                                  title="Remove characteristic"
                                >
                                  &times;
                                </button>
                              </div>
                            ))}
                            <div className="field-row">
                              <input
                                type="text"
                                value={newRawMaterialCharacteristic.name}
                                onChange={(e) =>
                                  setNewRawMaterialCharacteristic((prev) => ({ ...prev, name: e.target.value }))
                                }
                                placeholder="Add characteristic (e.g. Type, Color)"
                              />
                              <label className="toggle">
                                <input
                                  type="checkbox"
                                  checked={Boolean(newRawMaterialCharacteristic.mandatory)}
                                  onChange={(e) =>
                                    setNewRawMaterialCharacteristic((prev) => ({ ...prev, mandatory: e.target.checked }))
                                  }
                                />
                                Mandatory
                              </label>
                              <button
                                type="button"
                                className="primary-button"
                                onClick={() => addRawMaterialCharacteristic(index)}
                              >
                                Add Characteristic
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="raw-material-add">
                    <input
                      type="text"
                      value={newRawMaterial.name}
                      onChange={(e) => setNewRawMaterial({ ...newRawMaterial, name: e.target.value })}
                      placeholder="Category name"
                    />
                    <input
                      type="text"
                      value={newRawMaterial.unitLabel}
                      onChange={(e) => setNewRawMaterial({ ...newRawMaterial, unitLabel: e.target.value })}
                      placeholder="Unit label (e.g. Kg, pcs)"
                    />
                    <button type="button" className="primary-button" onClick={addRawMaterial}>
                      Add Raw Material
                    </button>
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="wizard-section">
                <h2>Forms and records</h2>
                <p>Shape the capture forms for transactions and products. People records stay available below as optional advanced setup.</p>
                <div className="field-builder">
                  <h3>Product fields</h3>
                  {productSchema.fields.length === 0 ? (
                    <p className="helper-text">No product fields yet.</p>
                  ) : (
                    <div className="field-list">
                      {productSchema.fields.map((field, index) => (
                        <div key={field.key} className="field-row">
                          <div className="field-order">
                            <button
                              type="button"
                              className="ghost-button"
                              onClick={() => moveProductField(field.key, 'up')}
                              disabled={index === 0}
                              title="Move up"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="ghost-button"
                              onClick={() => moveProductField(field.key, 'down')}
                              disabled={index === productSchema.fields.length - 1}
                              title="Move down"
                            >
                              ↓
                            </button>
                          </div>
                          <input
                            type="text"
                            value={field.label}
                            onChange={(e) => updateProductField(field.key, { label: e.target.value })}
                          />
                          <select
                            value={field.type}
                            onChange={(e) => {
                              const nextType = e.target.value;
                              updateProductField(field.key, {
                                type: nextType,
                                unit: nextType === 'number' ? (field.unit || '') : ''
                              });
                            }}
                          >
                            {FIELD_TYPES.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                          {field.type === 'number' && (
                            <input
                              type="text"
                              value={field.unit || ''}
                              onChange={(e) => updateProductField(field.key, { unit: e.target.value })}
                              placeholder="Unit (optional)"
                            />
                          )}
                          {field.type === 'dropdown' && (
                            <input
                              type="text"
                              value={(field.options || []).join(', ')}
                              onChange={(e) =>
                                updateProductField(field.key, {
                                  options: e.target.value.split(',').map((opt) => opt.trim()).filter(Boolean)
                                })
                              }
                              placeholder="Option 1, Option 2"
                            />
                          )}
                          <label className="toggle">
                            <input
                              type="checkbox"
                              checked={field.required}
                              onChange={(e) => updateProductField(field.key, { required: e.target.checked })}
                            />
                            Required
                          </label>
                          <button type="button" className="danger-link" onClick={() => removeProductField(field.key)}>
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="field-add">
                    <input
                      type="text"
                      value={newProductField.label}
                      onChange={(e) => setNewProductField({ ...newProductField, label: e.target.value })}
                      placeholder="Field label"
                    />
                    <select
                      value={newProductField.type}
                      onChange={(e) => {
                        const nextType = e.target.value;
                        setNewProductField({
                          ...newProductField,
                          type: nextType,
                          unit: nextType === 'number' ? newProductField.unit : ''
                        });
                      }}
                    >
                      {FIELD_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {newProductField.type === 'number' && (
                      <input
                        type="text"
                        value={newProductField.unit}
                        onChange={(e) => setNewProductField({ ...newProductField, unit: e.target.value })}
                        placeholder="Unit (optional)"
                      />
                    )}
                    {newProductField.type === 'dropdown' && (
                      <input
                        type="text"
                        value={newProductField.options}
                        onChange={(e) => setNewProductField({ ...newProductField, options: e.target.value })}
                        placeholder="Option 1, Option 2"
                      />
                    )}
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={newProductField.required}
                        onChange={(e) => setNewProductField({ ...newProductField, required: e.target.checked })}
                      />
                      Required
                    </label>
                    <button type="button" className="primary-button" onClick={addProductField}>
                      Add Field
                    </button>
                  </div>
                </div>

                <div className="field-builder">
                  <h3>Transaction fields</h3>
                  <p>Define separate forms for raw material and finished goods transactions.</p>
                  <div className="subtabs transaction-mode-switch" role="tablist" aria-label="Transaction schema mode" style={{ marginBottom: '14px' }}>
                    <button
                      type="button"
                      className={transactionSchemaMode === 'raw_material' ? 'active raw' : 'raw'}
                      onClick={() => setTransactionSchemaMode('raw_material')}
                    >
                      Raw Material Form
                    </button>
                    <button
                      type="button"
                      className={transactionSchemaMode === 'finished_goods' ? 'active finished' : 'finished'}
                      onClick={() => setTransactionSchemaMode('finished_goods')}
                    >
                      Finished Goods Form
                    </button>
                  </div>
                  <div className="wizard-grid">
                    {transactionSchemaMode === 'finished_goods' ? (
                      <label>
                        Quantity unit (required)
                        <input
                          type="text"
                          value={transactionSchema.quantity_unit}
                          onChange={(e) => updateSchema({ quantity_unit: e.target.value })}
                          placeholder="Pcs"
                        />
                      </label>
                    ) : (
                      <div>
                        <span className="helper-text">
                          Quantity unit for raw-material transactions comes from each raw material definition.
                        </span>
                      </div>
                    )}
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={(transactionSchema.stock_field_key || 'quantity') === 'quantity'}
                        onChange={(e) =>
                          updateSchema({ stock_field_key: e.target.checked ? 'quantity' : transactionSchema.stock_field_key })
                        }
                      />
                      Use Quantity as stock decider
                    </label>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={transactionSchema.include_date !== false}
                        onChange={(e) => updateSchema({ include_date: e.target.checked })}
                      />
                      Include date field
                    </label>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={transactionSchema.include_notes !== false}
                        onChange={(e) => updateSchema({ include_notes: e.target.checked })}
                      />
                      Include notes field
                    </label>
                  </div>

                  <div className="field-list">
                    {transactionSchema.fields.map((field, index) => (
                      <div key={field.key} className="field-row">
                        <div className="field-order">
                          <button
                            type="button"
                            className="ghost-button"
                            onClick={() => moveField(field.key, 'up')}
                            disabled={index === 0}
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="ghost-button"
                            onClick={() => moveField(field.key, 'down')}
                            disabled={index === transactionSchema.fields.length - 1}
                            title="Move down"
                          >
                            ↓
                          </button>
                        </div>
                        <input
                          type="text"
                          value={field.label}
                          onChange={(e) => updateField(field.key, { label: e.target.value })}
                          disabled={field.key === 'quantity' || field.key === 'type'}
                        />
                        <select
                          value={field.type}
                          onChange={(e) => {
                            const nextType = e.target.value;
                            updateField(field.key, {
                              type: nextType,
                              unit: nextType === 'number' ? (field.unit || '') : ''
                            });
                          }}
                          disabled={field.key === 'quantity' || field.key === 'type'}
                        >
                          {FIELD_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                        {field.type === 'number' && (
                          <input
                            type="text"
                            value={field.unit || ''}
                            onChange={(e) => updateField(field.key, { unit: e.target.value })}
                            placeholder="Unit (optional)"
                            disabled={field.key === 'quantity' || field.key === 'type'}
                          />
                        )}
                        {field.type === 'dropdown' && (
                          <input
                            type="text"
                            value={(field.options || []).join(', ')}
                            onChange={(e) =>
                              updateField(field.key, {
                                options: e.target.value.split(',').map((opt) => opt.trim()).filter(Boolean)
                              })
                            }
                            placeholder="Option 1, Option 2"
                            disabled={field.key === 'type' || transactionSchema.type_field_key === field.key}
                          />
                        )}
                        {field.type === 'dropdown' && (
                          <label className="toggle">
                            <input
                              type="checkbox"
                              checked={transactionSchema.type_field_key === field.key}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  updateField(field.key, { required: true, options: ['in', 'out'] });
                                  updateSchema({ type_field_key: field.key });
                                } else {
                                  updateSchema({ type_field_key: null });
                                }
                              }}
                            />
                            Transaction type
                          </label>
                        )}
                        {field.type === 'number' && (
                          <label className="toggle">
                            <input
                              type="checkbox"
                              checked={transactionSchema.stock_field_key === field.key}
                              onChange={(e) =>
                                updateSchema({
                                  stock_field_key: e.target.checked ? field.key : 'quantity'
                                })
                              }
                            />
                            Stock decider
                          </label>
                        )}
                        <label className="toggle">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) => updateField(field.key, { required: e.target.checked })}
                            disabled={field.key === 'quantity' || field.key === 'type'}
                          />
                          Required
                        </label>
                        {field.key !== 'quantity' && field.key !== 'type' && (
                          <button type="button" className="danger-link" onClick={() => removeField(field.key)}>
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="field-add">
                    <input
                      type="text"
                      value={newField.label}
                      onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                      placeholder="Field label"
                    />
                    <select
                      value={newField.type}
                      onChange={(e) => {
                        const nextType = e.target.value;
                        setNewField({
                          ...newField,
                          type: nextType,
                          unit: nextType === 'number' ? newField.unit : ''
                        });
                      }}
                    >
                      {FIELD_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {newField.type === 'number' && (
                      <input
                        type="text"
                        value={newField.unit}
                        onChange={(e) => setNewField({ ...newField, unit: e.target.value })}
                        placeholder="Unit (optional)"
                      />
                    )}
                    {newField.type === 'dropdown' && (
                      <input
                        type="text"
                        value={newField.options}
                        onChange={(e) => setNewField({ ...newField, options: e.target.value })}
                        placeholder="Option 1, Option 2"
                      />
                    )}
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={newField.required}
                        onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                      />
                      Required
                    </label>
                    <button type="button" className="primary-button" onClick={addField}>
                      Add Field
                    </button>
                  </div>
                </div>

                <div className="wizard-subsection">
                  <h3>People records</h3>
                  <p className="helper-text">Optional advanced setup for staff, customers, and contractors.</p>
                  <div className="wizard-stat-grid">
                    <div className="info-card">
                      <h4>Staff</h4>
                      <p>{buildFieldCountSummary(staffSchema)} extra fields</p>
                    </div>
                    <div className="info-card">
                      <h4>Customers</h4>
                      <p>{buildFieldCountSummary(customerSchema)} extra fields</p>
                    </div>
                    <div className="info-card">
                      <h4>Contractors</h4>
                      <p>{buildFieldCountSummary(contractorSchema)} extra fields</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="wizard-section">
                <details className="wizard-collapsible">
                  <summary>Staff fields</summary>
                  <p>Core fields (Name, Skill Category, ID Type, ID Value) are included by default. Add any extra fields below.</p>
                  <div className="field-builder wizard-inline-builder">
                    <h3>Staff schema</h3>
                    {staffSchema.fields.length === 0 ? (
                      <p className="helper-text">No extra staff fields yet.</p>
                    ) : (
                      <div className="field-list">
                        {staffSchema.fields.map((field, index) => (
                          <div key={field.key} className="field-row">
                            <div className="field-order">
                              <button
                                type="button"
                                className="ghost-button"
                                onClick={() => moveStaffField(field.key, 'up')}
                                disabled={index === 0}
                                title="Move up"
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="ghost-button"
                                onClick={() => moveStaffField(field.key, 'down')}
                                disabled={index === staffSchema.fields.length - 1}
                                title="Move down"
                              >
                                ↓
                              </button>
                            </div>
                            <input
                              type="text"
                              value={field.label}
                              onChange={(e) => updateStaffField(field.key, { label: e.target.value })}
                            />
                            <select
                              value={field.type}
                              onChange={(e) => {
                                const nextType = e.target.value;
                                updateStaffField(field.key, {
                                  type: nextType,
                                  unit: nextType === 'number' ? (field.unit || '') : ''
                                });
                              }}
                            >
                              {FIELD_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                            </select>
                            {field.type === 'number' && (
                              <input
                                type="text"
                                value={field.unit || ''}
                                onChange={(e) => updateStaffField(field.key, { unit: e.target.value })}
                                placeholder="Unit (optional)"
                              />
                            )}
                            {field.type === 'dropdown' && (
                              <input
                                type="text"
                                value={(field.options || []).join(', ')}
                                onChange={(e) =>
                                  updateStaffField(field.key, {
                                    options: e.target.value.split(',').map((opt) => opt.trim()).filter(Boolean)
                                  })
                                }
                                placeholder="Option 1, Option 2"
                              />
                            )}
                            <label className="toggle">
                              <input
                                type="checkbox"
                                checked={field.required}
                                onChange={(e) => updateStaffField(field.key, { required: e.target.checked })}
                              />
                              Required
                            </label>
                            <button type="button" className="danger-link" onClick={() => removeStaffField(field.key)}>
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="field-add">
                      <input
                        type="text"
                        value={newStaffField.label}
                        onChange={(e) => setNewStaffField({ ...newStaffField, label: e.target.value })}
                        placeholder="Field label"
                      />
                      <select
                        value={newStaffField.type}
                        onChange={(e) => {
                          const nextType = e.target.value;
                          setNewStaffField({
                            ...newStaffField,
                            type: nextType,
                            unit: nextType === 'number' ? newStaffField.unit : ''
                          });
                        }}
                      >
                        {FIELD_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      {newStaffField.type === 'number' && (
                        <input
                          type="text"
                          value={newStaffField.unit}
                          onChange={(e) => setNewStaffField({ ...newStaffField, unit: e.target.value })}
                          placeholder="Unit (optional)"
                        />
                      )}
                      {newStaffField.type === 'dropdown' && (
                        <input
                          type="text"
                          value={newStaffField.options}
                          onChange={(e) => setNewStaffField({ ...newStaffField, options: e.target.value })}
                          placeholder="Option 1, Option 2"
                        />
                      )}
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={newStaffField.required}
                          onChange={(e) => setNewStaffField({ ...newStaffField, required: e.target.checked })}
                        />
                        Required
                      </label>
                      <button type="button" className="primary-button" onClick={addStaffField}>
                        Add Field
                      </button>
                    </div>
                    <p className="helper-text">Dropdown options can be managed later from Settings → Manage Lists.</p>
                  </div>
                </details>
              </div>
            )}

            {step === 4 && (
              <div className="wizard-section">
                <details className="wizard-collapsible">
                  <summary>Customer fields</summary>
                  <p>Define the fields captured for each customer.</p>
                  <div className="field-builder wizard-inline-builder">
                    <h3>Customer schema</h3>
                  {customerSchema.fields.length === 0 ? (
                    <p className="helper-text">No customer fields yet.</p>
                  ) : (
                    <div className="field-list">
                      {customerSchema.fields.map((field, index) => (
                        <div key={field.key} className="field-row">
                          <div className="field-order">
                            <button
                              type="button"
                              className="ghost-button"
                              onClick={() => moveCustomerField(field.key, 'up')}
                              disabled={index === 0}
                              title="Move up"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="ghost-button"
                              onClick={() => moveCustomerField(field.key, 'down')}
                              disabled={index === customerSchema.fields.length - 1}
                              title="Move down"
                            >
                              ↓
                            </button>
                          </div>
                          <input
                            type="text"
                            value={field.label}
                            onChange={(e) => updateCustomerField(field.key, { label: e.target.value })}
                          />
                          <select
                            value={field.type}
                            onChange={(e) => {
                              const nextType = e.target.value;
                              updateCustomerField(field.key, {
                                type: nextType,
                                unit: nextType === 'number' ? (field.unit || '') : ''
                              });
                            }}
                          >
                            {FIELD_TYPES.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                          {field.type === 'number' && (
                            <input
                              type="text"
                              value={field.unit || ''}
                              onChange={(e) => updateCustomerField(field.key, { unit: e.target.value })}
                              placeholder="Unit (optional)"
                            />
                          )}
                          {field.type === 'dropdown' && (
                            <input
                              type="text"
                              value={(field.options || []).join(', ')}
                              onChange={(e) =>
                                updateCustomerField(field.key, {
                                  options: e.target.value.split(',').map((opt) => opt.trim()).filter(Boolean)
                                })
                              }
                              placeholder="Option 1, Option 2"
                            />
                          )}
                          <label className="toggle">
                            <input
                              type="checkbox"
                              checked={field.required}
                              onChange={(e) => updateCustomerField(field.key, { required: e.target.checked })}
                            />
                            Required
                          </label>
                          <button type="button" className="danger-link" onClick={() => removeCustomerField(field.key)}>
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="field-add">
                    <input
                      type="text"
                      value={newCustomerField.label}
                      onChange={(e) => setNewCustomerField({ ...newCustomerField, label: e.target.value })}
                      placeholder="Field label"
                    />
                    <select
                      value={newCustomerField.type}
                      onChange={(e) => {
                        const nextType = e.target.value;
                        setNewCustomerField({
                          ...newCustomerField,
                          type: nextType,
                          unit: nextType === 'number' ? newCustomerField.unit : ''
                        });
                      }}
                    >
                      {FIELD_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {newCustomerField.type === 'number' && (
                      <input
                        type="text"
                        value={newCustomerField.unit}
                        onChange={(e) => setNewCustomerField({ ...newCustomerField, unit: e.target.value })}
                        placeholder="Unit (optional)"
                      />
                    )}
                    {newCustomerField.type === 'dropdown' && (
                      <input
                        type="text"
                        value={newCustomerField.options}
                        onChange={(e) => setNewCustomerField({ ...newCustomerField, options: e.target.value })}
                        placeholder="Option 1, Option 2"
                      />
                    )}
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={newCustomerField.required}
                        onChange={(e) => setNewCustomerField({ ...newCustomerField, required: e.target.checked })}
                      />
                      Required
                    </label>
                    <button type="button" className="primary-button" onClick={addCustomerField}>
                      Add Field
                    </button>
                  </div>
                    <p className="helper-text">Dropdown options can be managed later from Settings → Manage Lists.</p>
                  </div>
                </details>
              </div>
            )}

            {step === 4 && (
              <div className="wizard-section">
                <details className="wizard-collapsible">
                  <summary>Contractor fields</summary>
                  <p>Define the fields captured for each contractor.</p>
                  <div className="field-builder wizard-inline-builder">
                    <h3>Contractor schema</h3>
                  {contractorSchema.fields.length === 0 ? (
                    <p className="helper-text">No contractor fields yet.</p>
                  ) : (
                    <div className="field-list">
                      {contractorSchema.fields.map((field, index) => (
                        <div key={field.key} className="field-row">
                          <div className="field-order">
                            <button
                              type="button"
                              className="ghost-button"
                              onClick={() => moveContractorField(field.key, 'up')}
                              disabled={index === 0}
                              title="Move up"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="ghost-button"
                              onClick={() => moveContractorField(field.key, 'down')}
                              disabled={index === contractorSchema.fields.length - 1}
                              title="Move down"
                            >
                              ↓
                            </button>
                          </div>
                          <input
                            type="text"
                            value={field.label}
                            onChange={(e) => updateContractorField(field.key, { label: e.target.value })}
                          />
                          <select
                            value={field.type}
                            onChange={(e) => {
                              const nextType = e.target.value;
                              updateContractorField(field.key, {
                                type: nextType,
                                unit: nextType === 'number' ? (field.unit || '') : ''
                              });
                            }}
                          >
                            {FIELD_TYPES.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                          {field.type === 'number' && (
                            <input
                              type="text"
                              value={field.unit || ''}
                              onChange={(e) => updateContractorField(field.key, { unit: e.target.value })}
                              placeholder="Unit (optional)"
                            />
                          )}
                          {field.type === 'dropdown' && (
                            <input
                              type="text"
                              value={(field.options || []).join(', ')}
                              onChange={(e) =>
                                updateContractorField(field.key, {
                                  options: e.target.value.split(',').map((opt) => opt.trim()).filter(Boolean)
                                })
                              }
                              placeholder="Option 1, Option 2"
                            />
                          )}
                          <label className="toggle">
                            <input
                              type="checkbox"
                              checked={field.required}
                              onChange={(e) => updateContractorField(field.key, { required: e.target.checked })}
                            />
                            Required
                          </label>
                          <button type="button" className="danger-link" onClick={() => removeContractorField(field.key)}>
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="field-add">
                    <input
                      type="text"
                      value={newContractorField.label}
                      onChange={(e) => setNewContractorField({ ...newContractorField, label: e.target.value })}
                      placeholder="Field label"
                    />
                    <select
                      value={newContractorField.type}
                      onChange={(e) => {
                        const nextType = e.target.value;
                        setNewContractorField({
                          ...newContractorField,
                          type: nextType,
                          unit: nextType === 'number' ? newContractorField.unit : ''
                        });
                      }}
                    >
                      {FIELD_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {newContractorField.type === 'number' && (
                      <input
                        type="text"
                        value={newContractorField.unit}
                        onChange={(e) => setNewContractorField({ ...newContractorField, unit: e.target.value })}
                        placeholder="Unit (optional)"
                      />
                    )}
                    {newContractorField.type === 'dropdown' && (
                      <input
                        type="text"
                        value={newContractorField.options}
                        onChange={(e) => setNewContractorField({ ...newContractorField, options: e.target.value })}
                        placeholder="Option 1, Option 2"
                      />
                    )}
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={newContractorField.required}
                        onChange={(e) => setNewContractorField({ ...newContractorField, required: e.target.checked })}
                      />
                      Required
                    </label>
                    <button type="button" className="primary-button" onClick={addContractorField}>
                      Add Field
                    </button>
                  </div>
                    <p className="helper-text">Dropdown options can be managed later from Settings → Manage Lists.</p>
                  </div>
                </details>
              </div>
            )}

            {step === 5 && (
              <div className="wizard-section">
                <h2>Access accounts</h2>
                <div className="wizard-grid">
                  <div className="info-card">
                    <h3>Primary owner credentials</h3>
                    <p><strong>Username:</strong> owner</p>
                    <p><strong>Temporary password:</strong> owner123</p>
                    <p>This account has unrestricted access within the tabs enabled for the company.</p>
                  </div>
                  <div className="info-card">
                    <h3>Admin credentials</h3>
                    <p><strong>Username:</strong> admin</p>
                    <p><strong>Temporary password:</strong> admin123</p>
                    <p>Use this for company admins who should manage users and settings without being the owner account.</p>
                  </div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={admin.forceReset}
                      onChange={(e) => setAdmin({ ...admin, forceReset: e.target.checked })}
                    />
                    Force password reset on first login
                  </label>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={company.activateNow}
                      onChange={(e) => setCompany({ ...company, activateNow: e.target.checked })}
                    />
                    Activate company immediately after setup
                  </label>
                </div>
              </div>
            )}

            {step === 6 && (
              <div className="wizard-section">
                <h2>Review & create</h2>
                <div className="review-grid">
                  <div>
                    <span className="label">Company</span>
                    <p>{company.name || '—'}</p>
                    <p>{company.slug || 'Auto-generated'}</p>
                    <p>{company.timezone}</p>
                  </div>
                  <div>
                    <span className="label">Start mode</span>
                    <p>{setupMode === 'clone' ? 'Cloned from existing company' : 'Started fresh'}</p>
                    <p>
                      {setupMode === 'clone' && selectedTemplateCompany
                        ? `${selectedTemplateCompany.name} (${selectedTemplateCompany.slug})`
                        : selectedIndustryPreset
                          ? `Preset: ${selectedIndustryPreset.label}`
                          : 'No preset selected'}
                    </p>
                  </div>
                  <div>
                    <span className="label">Workspace</span>
                    <p>{enabledTabCount} tabs enabled</p>
                    <p>
                      {dashboardTabs.filter((tab) => tab.visible !== false).map((tab) => tab.label).join(', ') || 'None'}
                    </p>
                  </div>
                  <div>
                    <span className="label">Thresholds</span>
                    <p>Critical: {defaults.critical}</p>
                    <p>Low: {defaults.low}</p>
                    <p>Medium: {defaults.medium}</p>
                  </div>
                  <div>
                    <span className="label">Orders</span>
                    <p>Order start: {defaults.orderNumberStart}</p>
                  </div>
                  <div>
                    <span className="label">Process pipeline</span>
                    <p>{pipelineStages.length ? pipelineStages.join(' → ') : 'Not set'}</p>
                  </div>
                  <div>
                    <span className="label">Raw materials</span>
                    {rawMaterials.length ? (
                      rawMaterials.map((item, index) => (
                        <p key={`${item.name}-${index}`}>
                          {item.name}{item.is_main ? ' (Main)' : ''}
                          {item.unit_label ? ` (Unit: ${item.unit_label})` : ''}
                          {item.characteristics?.length
                            ? ` — ${item.characteristics
                              .map((char) => `${char.name}${char.mandatory ? ' *' : ''}`)
                              .join(', ')}`
                            : ''}
                        </p>
                      ))
                    ) : (
                      <p>Not set</p>
                    )}
                  </div>
                  <div>
                    <span className="label">Raw material transaction form</span>
                    <p>Quantity unit: {rawMaterialTransactionSchema.quantity_unit || 'Kg'}</p>
                    <p>Date: {rawMaterialTransactionSchema.include_date !== false ? 'Included' : 'Hidden'}</p>
                    <p>Notes: {rawMaterialTransactionSchema.include_notes !== false ? 'Included' : 'Hidden'}</p>
                    <p>Custom fields: {rawMaterialTransactionSchema.fields.length}</p>
                  </div>
                  <div>
                    <span className="label">Finished goods transaction form</span>
                    <p>Quantity unit: {finishedGoodsTransactionSchema.quantity_unit || 'Pcs'}</p>
                    <p>Date: {finishedGoodsTransactionSchema.include_date !== false ? 'Included' : 'Hidden'}</p>
                    <p>Notes: {finishedGoodsTransactionSchema.include_notes !== false ? 'Included' : 'Hidden'}</p>
                    <p>Custom fields: {finishedGoodsTransactionSchema.fields.length}</p>
                  </div>
                  <div>
                    <span className="label">Product fields</span>
                    <p>Custom fields: {productSchema.fields.length}</p>
                  </div>
                  <div>
                    <span className="label">People records</span>
                    <p>Staff fields: {staffSchema.fields.length}</p>
                    <p>Customer fields: {customerSchema.fields.length}</p>
                    <p>Contractor fields: {contractorSchema.fields.length}</p>
                    <p>Total extra fields: {peopleFieldCount}</p>
                  </div>
                  <div>
                    <span className="label">Activation</span>
                    <p>{company.activateNow ? 'Active' : 'Disabled (locked)'} </p>
                    <p>Force reset: {admin.forceReset ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <aside className="wizard-preview">
            <div className="wizard-preview-shell">
              <div className="wizard-preview-sidebar">
                <div className="preview-brand">
                  <img
                    src={logoPreview || '/default-logo.svg'}
                    alt="Logo"
                  />
                  <div>
                    <h3>{company.name || 'New Company'}</h3>
                    <p>{company.slug ? `@${company.slug}` : 'Preview dashboard'}</p>
                  </div>
                </div>
                <div className="preview-nav">
                  {previewTabsFiltered.length > 0 ? (
                    previewTabsFiltered.map((tab, index) => (
                      <div
                        key={tab.id}
                        className={`preview-nav-item ${index === 0 ? 'active' : ''}`}
                      >
                        <span className={`preview-nav-icon preview-nav-icon-${tab.id}`}>
                          {renderPreviewIcon(tab.id)}
                        </span>
                        {tab.label}
                      </div>
                    ))
                  ) : (
                    <div className="preview-nav-empty">No tabs enabled yet.</div>
                  )}
                </div>
                <div className="preview-sidebar-footer">
                  <span className={`preview-status ${company.activateNow ? 'active' : 'disabled'}`}>
                    {company.activateNow ? 'Active' : 'Disabled'}
                  </span>
                  <div className="preview-pipeline">
                    <span>Pipeline</span>
                    <strong>{pipelineStages.length ? pipelineStages.join(' → ') : 'Not set'}</strong>
                  </div>
                </div>
              </div>
              <div className="wizard-preview-main">
                <div className="wizard-preview-header">
                  <h4>Admin Dashboard</h4>
                  <span>{steps[step]}</span>
                </div>
                <div className="wizard-preview-content">
                  <div className="preview-metric">
                    <span>Visible tabs</span>
                    <strong>{enabledTabCount}</strong>
                  </div>
                  <div className="preview-metric">
                    <span>Start mode</span>
                    <strong>{setupMode === 'clone' ? 'Cloned' : (selectedIndustryPreset?.label || 'Fresh')}</strong>
                  </div>
                  <div className="preview-metric">
                    <span>Raw materials</span>
                    <strong>{rawMaterials.length}</strong>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <div className="wizard-footer">
        <button type="button" className="secondary-button" onClick={handleBack} disabled={step === 0}>
          Back
        </button>
        {step < steps.length - 1 ? (
          <button type="button" className="primary-button" onClick={handleNext}>
            Next
          </button>
        ) : (
          <button type="button" className="primary-button" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating...' : 'Create Company'}
          </button>
        )}
      </div>
    </div>
  );
}

export default CompanySetupWizard;
